import React from 'react';
import cnames from 'classnames';
import {
  PRIMARY,
  EXPRESSIVE,
  DESTRUCTIVE,
  TOGGLE,
  NEUTRAL,
  PLAIN,
  SMALL,
  MEDIUM,
  LARGE,
  XLARGE,
  ELEVATION_LOW,
  ELEVATION_MEDIUM,
  ELEVATION_HIGH,
} from '../constants';
import _styles from './styles.module.css';

interface LoadingStateProps {
  styles: Record<string, string>;
}

const LoadingState = ({ styles }: LoadingStateProps) => (
  <span className={styles.loadingState}>
    <span className={styles.bounce} />
    <span className={styles.bounce} />
  </span>
);

type ButtonIntent = typeof PRIMARY | typeof EXPRESSIVE | typeof DESTRUCTIVE
  | typeof TOGGLE | typeof NEUTRAL | typeof PLAIN;
type ButtonSize = typeof SMALL | typeof MEDIUM | typeof LARGE | typeof XLARGE;
type ButtonElevation = typeof ELEVATION_LOW
  | typeof ELEVATION_MEDIUM | typeof ELEVATION_HIGH | null;

interface ClassNameProps {
  fluid?: boolean;
  intent?: ButtonIntent;
  elevation?: ButtonElevation;
  size?: ButtonSize;
  circle?: boolean;
  disabled?: boolean;
  strong?: boolean;
  border?: boolean;
  active?: boolean;
  inverse?: boolean;
  loading?: boolean;
  styles: Record<string, string>;
  fontSize?: string;
}

const getClassNames = (props: ClassNameProps) => {
  const {
    fluid,
    intent,
    elevation,
    size,
    circle,
    disabled,
    strong,
    border,
    active,
    inverse,
    loading,
    styles,
    fontSize,
  } = props;

  return cnames(styles.button, {
    [styles.primary]: intent === PRIMARY,
    [styles.expressive]: intent === EXPRESSIVE,
    [styles.destructive]: intent === DESTRUCTIVE,
    [styles.neutral]: intent === NEUTRAL,
    [styles.toggle]: intent === TOGGLE,
    [styles.plain]: intent === PLAIN,
    [styles.elevation]: !!elevation,
    [styles.elevationLow]: elevation === ELEVATION_LOW,
    [styles.elevationMedium]: elevation === ELEVATION_MEDIUM,
    [styles.elevationHigh]: elevation === ELEVATION_HIGH,
    [styles.circle]: circle,
    [styles.fluid]: fluid && !circle,
    [styles.loading]: loading,
    [styles.strong]: strong,
    [styles.border]: !strong && border,
    [styles.active]: active,
    [styles.inverse]: inverse,
    [styles.small]: size === SMALL,
    [styles.medium]: size === MEDIUM,
    [styles.large]: size === LARGE,
    [styles.xlarge]: size === XLARGE,
    [styles.disabled]: disabled,
    [styles[fontSize ?? '']]: !!fontSize,
  });
};

interface PreventDefaultProps {
  href?: string | null;
  disabled?: boolean;
}

const preventDefault = ({ href, disabled }: PreventDefaultProps) => href === '' || disabled;

interface ButtonProps {
  children?: React.ReactNode;
  theme?: unknown;
  intent?: ButtonIntent;
  size?: ButtonSize;
  loading?: boolean;
  iconBefore?: React.ReactNode;
  iconAfter?: React.ReactNode;
  target?: string;
  href?: string | null;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  styles?: Record<string, string>;
  disabled?: boolean;
  strong?: boolean;
  border?: boolean;
  fluid?: boolean;
  inverse?: boolean;
  elevation?: ButtonElevation;
  active?: boolean;
  circle?: boolean;
  onClick?: ((e: React.MouseEvent<HTMLElement>) => void) | null;
  innerRef?: React.Ref<HTMLButtonElement | HTMLAnchorElement>;
  fontSize?: string;
  [key: string]: unknown;
}

const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>((props, ref) => {
  const {
    children = '',
    theme,
    intent = NEUTRAL,
    size = MEDIUM,
    loading = false,
    iconBefore = null,
    iconAfter = null,
    target = '_self',
    href = null,
    type = 'button',
    className,
    styles = _styles,
    disabled = false,
    strong = false,
    border = false,
    fluid = false,
    inverse = false,
    elevation = null,
    active = false,
    circle = false,
    onClick = null,
    innerRef,
    ...rest
  } = props;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (preventDefault({ href, disabled })) {
      e.preventDefault();
    }

    if (disabled) {
      return;
    }

    if (typeof onClick === 'function') {
      onClick(e);
    }
  };

  const relAttribute = target === '_blank' ? 'noopener noreferrer' : undefined;

  const buttonChildren = (
    <React.Fragment>
      {loading && <LoadingState styles={styles} />}
      {(iconBefore || iconAfter) && (
        <React.Fragment>
          {iconBefore && (
            <span className={styles.iconBefore}>{iconBefore}</span>
          )}
          {children && <span className={styles.children}>{children}</span>}
          {iconAfter && <span className={styles.iconAfter}>{iconAfter}</span>}
        </React.Fragment>
      )}
      {!iconBefore && !iconAfter && (
        <span className={styles.children}>{children}</span>
      )}
    </React.Fragment>
  );

  const classNameProps: ClassNameProps = {
    fluid, intent, elevation, size, circle, disabled, strong, border, active, inverse, loading, styles, fontSize: rest.fontSize as string | undefined,
  };

  // eslint-disable-next-line react/button-has-type
  return href ? (
    <a
      // `disabled` is not a valid <a> attribute, but is passed through for legacy styling hooks
      {...({ disabled: !!disabled } as Record<string, boolean>)}
      onClick={handleClick}
      target={target}
      href={href}
      rel={relAttribute}
      ref={(ref || innerRef) as React.Ref<HTMLAnchorElement>}
      {...rest}
      className={getClassNames(classNameProps)}
    >
      {buttonChildren}
    </a>
  ) : (
    // eslint-disable-next-line react/button-has-type
    <button
      onClick={handleClick}
      disabled={!!disabled}
      type={type}
      ref={(ref || innerRef) as React.Ref<HTMLButtonElement>}
      {...rest}
      className={getClassNames(classNameProps)}
    >
      {buttonChildren}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
